import type { ApplyWorkspacePresetRequest } from "@/app/generated/api/schemas";
import {
  isRestaurantConfiguration,
  restaurantPresetCompatibilityConfiguration,
} from "./restaurantPresetApplySetup";

type PresetConfigurationEntry = {
  create: () => ApplyWorkspacePresetRequest["configuration"];
  isValid: (value: unknown) => boolean;
};

const appBundleConfiguration: PresetConfigurationEntry = {
  create: () => ({}),
  isValid: isEmptyConfiguration,
};

const restaurantConfiguration: PresetConfigurationEntry = {
  create: () => restaurantPresetCompatibilityConfiguration,
  isValid: isRestaurantConfiguration,
};

const presetConfigurationRegistry = {
  restaurant: restaurantConfiguration,
  courier: appBundleConfiguration,
  "agricultural-store": appBundleConfiguration,
  "arts-and-crafts-shop": appBundleConfiguration,
  "auto-service": appBundleConfiguration,
  bakery: appBundleConfiguration,
  "bar-and-pub": appBundleConfiguration,
  "beauty-parlor": appBundleConfiguration,
  bookstore: appBundleConfiguration,
  carpenter: appBundleConfiguration,
  "clothing-store": appBundleConfiguration,
  "cosmetics-store": appBundleConfiguration,
  "electronics-store": appBundleConfiguration,
  "eyewear-store": appBundleConfiguration,
  "fast-food": appBundleConfiguration,
  "fitness-center": appBundleConfiguration,
  "furniture-store": appBundleConfiguration,
  gardening: appBundleConfiguration,
  "hair-salon": appBundleConfiguration,
  hotel: appBundleConfiguration,
  library: appBundleConfiguration,
  "marketing-agency": appBundleConfiguration,
  "personal-trainer": appBundleConfiguration,
  pharmacy: appBundleConfiguration,
  plumber: appBundleConfiguration,
  "real-estate-agency": appBundleConfiguration,
  "spa-resort": appBundleConfiguration,
  "tattoo-shop": appBundleConfiguration,
  "tire-service": appBundleConfiguration,
  "toy-store": appBundleConfiguration,
  tradesperson: appBundleConfiguration,
  "wine-shop": appBundleConfiguration,
} as const satisfies Record<string, PresetConfigurationEntry>;

export type ConfigurablePresetCode = keyof typeof presetConfigurationRegistry;

export function isConfigurablePresetCode(value: string): value is ConfigurablePresetCode {
  return Object.hasOwn(presetConfigurationRegistry, value);
}

export function presetApplyConfiguration(
  presetCode: string,
): ApplyWorkspacePresetRequest["configuration"] {
  if (isConfigurablePresetCode(presetCode)) {
    return presetConfigurationRegistry[presetCode].create();
  }

  throw new Error(`Unsupported preset configuration: ${presetCode}`);
}

export function isPresetConfiguration(
  presetCode: string,
  value: unknown,
): value is ApplyWorkspacePresetRequest["configuration"] {
  if (isConfigurablePresetCode(presetCode)) {
    return presetConfigurationRegistry[presetCode].isValid(value);
  }

  return false;
}

function isEmptyConfiguration(value: unknown): boolean {
  return value !== null
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.keys(value).length === 0;
}
